import { useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Routes, Route, useNavigate } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { TitleProvider } from './contexts/TitleContext';
import Home from './pages/Home';
import Chat from './pages/Chat';
import Settings from './pages/Settings';
import AutocompleteRedirect from './pages/AutocompleteRedirect';
import ProductRedirect from './pages/ProductRedirect';
import AgentCompare from './pages/AgentCompare';
import ProtectedRoute from './components/ProtectedRoute';
import { hasInternalAgentHistory, initializeWebView, isWebView, returnToBasalam } from './services/webviewService';
import { ChatHeader } from './components/Navbar';
import { setCartQueryClient } from './services/cartService';

// Create a client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Use cached data, don't refetch on mount
      retry: 1,
      gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep cache for persistence
      staleTime: 1000 * 60 * 5, // 5 minutes default stale time
    },
  },
});

// Set query client for cart service to use for invalidation
setCartQueryClient(queryClient);

// Create a persister to store cache in localStorage (survives WebView recreation)
const persister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: 'ai-agent-query-cache',
});

// Max age for persisted cache (24 hours)
const maxAge = 1000 * 60 * 60 * 24;
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

function AppContent() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isWebView()) {
      return;
    }

    return initializeWebView({
      onBack: () => {
        if (hasInternalAgentHistory(window.history.state)) {
          navigate(-1);
          return;
        }
        returnToBasalam();
      },
    });
  }, [navigate]);


  return (
    <div className="grid h-dvh min-h-0 grid-cols-1 grid-rows-[58px_minmax(0,1fr)] overflow-hidden bg-ds-canvas text-ds-ink app-sm:grid-rows-[72px_minmax(0,1fr)] app-lg:grid-cols-[260px_minmax(0,1fr)]">
      <ChatHeader />
      <main className="col-start-1 row-start-2 min-h-0 min-w-0 overflow-hidden app-lg:col-start-2">
        <Routes>
          <Route path="/" element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          } />
          <Route path="/chat/:id" element={
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />
          {IS_DEVELOPMENT && <Route path="/agents/compare" element={<AgentCompare />} />}
          <Route path="/entry/autocomplete" element={<AutocompleteRedirect />} />
          <Route path="/entry/:product_id" element={<ProductRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <PersistQueryClientProvider 
      client={queryClient} 
      persistOptions={{ 
        persister,
        maxAge, // Don't restore cache older than 24 hours
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            // Only persist successful queries
            return query.state.status === 'success';
          },
        },
      }}
    >
      <ThemeProvider>
        <AuthProvider>
          <TitleProvider>
            <Router>
              <AppContent />
            </Router>
          </TitleProvider>
        </AuthProvider>
      </ThemeProvider>
    </PersistQueryClientProvider>
  );
}

export default App;
